<template>
    <div class="horizontal gallery-container">
        <div id="info-container">
            <div>
                <!-- NAME -->
                <div class="info-field horizontal">
                    <tr class="info-field-row">
                        <td class="title-td" style="width:140px;"><p class="info-field-title">{{ $t('ReportName') }}</p></td>
                        <td class="info-field-value">
                            <input class="datalist-input" style="width: 100%" v-model="report.name"/>
                        </td>
                    </tr>
                </div>
                <!-- ACTIVE -->
                <div class="info-field horizontal">
                    <tr class="info-field-row">
                        <td class="title-td" style="width:140px;"><p class="info-field-title">{{ $t('Active') }}</p></td>
                        <td class="info-field-value">
                            <input type="checkbox" class="crfi-checkbox" v-model="report.isActive"/>
                        </td>
                    </tr>
                </div>
                <!-- FREQUENCY -->
                <div class="info-field horizontal">
                    <tr class="info-field-row">
                        <td class="title-td" style="width:140px;"><p class="info-field-title">{{ $t('Frequency') }}</p></td>
                        <td class="info-field-value">
                            <select v-model="report.frequency">
                                <option :value="0">{{ $t('Daily') }}</option>
                                <option :value="1">{{ $t('Weekly') }}</option>
                                <option :value="2">{{ $t('Monthly') }}</option>
                            </select>
                        </td>
                    </tr>
                </div>
                <!-- DAY OF WEEK (weekly only) -->
                <div class="info-field horizontal" v-if="report.frequency === 1">
                    <tr class="info-field-row">
                        <td class="title-td" style="width:140px;"><p class="info-field-title">{{ $t('DayOfWeek') }}</p></td>
                        <td class="info-field-value">
                            <select v-model="report.dayOfWeek">
                                <option v-for="day in weekDays" :key="day.value" :value="day.value">{{ $t(day.label) }}</option>
                            </select>
                        </td>
                    </tr>
                </div>
                <!-- DAY OF MONTH (monthly only) -->
                <div class="info-field horizontal" v-if="report.frequency === 2">
                    <tr class="info-field-row">
                        <td class="title-td" style="width:140px;"><p class="info-field-title">{{ $t('DayOfMonth') }}</p></td>
                        <td class="info-field-value">
                            <select v-model="report.dayOfMonth">
                                <option v-for="day in monthDays" :key="day" :value="day">{{ day }}</option>
                            </select>
                        </td>
                    </tr>
                </div>
                <!-- SKIP WEEKENDS -->
                <div class="info-field horizontal">
                    <tr class="info-field-row">
                        <td class="title-td" style="width:140px;"><p class="info-field-title">{{ $t('SkipWeekends') }}</p></td>
                        <td class="info-field-value">
                            <input type="checkbox" class="crfi-checkbox" v-model="report.skipWeekends"/>
                        </td>
                    </tr>
                </div>
                <!-- SEND TIME -->
                <div class="info-field horizontal">
                    <tr class="info-field-row">
                        <td class="title-td" style="width:140px;"><p class="info-field-title">{{ $t('SendTime') }}</p></td>
                        <td class="info-field-value">
                            <div class="horizontal" style="align-items: center;">
                                <select v-model="report.sendHour" style="width: 60px;">
                                    <option v-for="hour in hours" :key="hour" :value="hour">{{ pad(hour) }}</option>
                                </select>
                                <span style="margin: 0 4px;">:</span>
                                <select v-model="report.sendMinute" style="width: 60px;">
                                    <option v-for="minute in minutes" :key="minute" :value="minute">{{ pad(minute) }}</option>
                                </select>
                            </div>
                        </td>
                    </tr>
                </div>
                <!-- LANGUAGE -->
                <div class="info-field horizontal">
                    <tr class="info-field-row">
                        <td class="title-td" style="width:140px;"><p class="info-field-title">{{ $t('ReportLanguage') }}</p></td>
                        <td class="info-field-value">
                            <select v-model="report.language">
                                <option :value="0">English</option>
                                <option :value="1">Norwegian</option>
                            </select>
                        </td>
                    </tr>
                </div>
                <p class="hint">{{ $t(periodHint) }}</p>
                <p class="hint" v-if="skipWeekendsHint !== ''">{{ $t(skipWeekendsHint) }}</p>
                <div>
                    <div class="error" v-if="error != ''">{{ error }}</div>
                    <div class="success" v-if="message != ''">{{ message }}</div>
                </div>
            </div>
            <div class="vertical" style="align-self: center;">
                <Button :title="'Save'" buttonClass='save-button semi-bold' style="width: 260px;" @onClick="save"></Button>
                <Button v-if="report.id" :title="'PreviewReport'" buttonClass='cancel-button semi-bold' style="width: 260px; margin-top: 8px;" @onClick="preview"></Button>
                <Button v-if="report.id" :title="'SendNow'" buttonClass='cancel-button semi-bold' style="width: 260px; margin-top: 8px;" @onClick="sendNow"></Button>
            </div>
        </div>

        <div id="recipients-container">
            <h4>{{ $t('Recipients') }}</h4>
            <div class="grid-box">
                <ejs-grid ref="users_grid" height='100%' width='100%' :selectionSettings='selectionOptions'
                          :allowTextWrap="true" :textWrapSettings='wrapSettings' :dataSource="users"
                          :allowFiltering='true' :filterSettings='filterOptions' :allowSorting='true'
                          :showBusyIndicator="true" :dataBound="restoreUsersSelection"
                          :rowSelected="usersSelectionChanged" :rowDeselected="usersSelectionChanged">
                    <e-columns>
                        <e-column type='checkbox' width='50' textAlign='Left'></e-column>
                        <e-column field='fullName' :headerText="$t('Name').toUpperCase()" textAlign='Left'></e-column>
                        <e-column field='email' :headerText="$t('Email').toUpperCase()" textAlign='Left'></e-column>
                    </e-columns>
                </ejs-grid>
            </div>
            <h4>{{ $t('ExternalEmails') }}</h4>
            <div class="horizontal" style="width: 90%; margin: auto; align-items: center;">
                <input class="datalist-input" type="email" style="flex: 1;" v-model="externalEmailInput"
                       :placeholder="$t('Email')" @keyup.enter="addExternalEmail"/>
                <Button :title="'AddEmail'" buttonClass='save-button semi-bold' style="margin-left: 8px;" @onClick="addExternalEmail"></Button>
            </div>
            <div class="chips" style="width: 90%; margin: 8px auto;">
                <span class="chip" v-for="email in externalEmails" :key="email">
                    {{ email }}
                    <span class="chip-remove" @click="removeExternalEmail(email)">&times;</span>
                </span>
            </div>
        </div>

        <div id="systems-container">
            <h4>{{ $t('Systems') }}</h4>
            <div class="grid-box">
                <ejs-grid ref="systems_grid" height='100%' width='100%' :selectionSettings='selectionOptions'
                          :allowTextWrap="true" :textWrapSettings='wrapSettings' :dataSource="systems"
                          :allowFiltering='true' :filterSettings='filterOptions' :allowSorting='true'
                          :showBusyIndicator="true" :dataBound="restoreSystemsSelection"
                          :rowSelected="systemsSelectionChanged" :rowDeselected="systemsSelectionChanged">
                    <e-columns>
                        <e-column type='checkbox' width='50' textAlign='Left'></e-column>
                        <e-column field='systemName' :headerText="$t('SystemName').toUpperCase()" textAlign='Left'></e-column>
                        <e-column field='country' :headerText="$t('Country').toUpperCase()" textAlign='Left' :filter="filterCheckbox"></e-column>
                    </e-columns>
                </ejs-grid>
            </div>
            <h4>{{ $t('Groups') }}</h4>
            <div class="grid-box">
                <ejs-grid ref="groups_grid" height='100%' width='100%' :selectionSettings='selectionOptions'
                          :allowTextWrap="true" :textWrapSettings='wrapSettings' :dataSource="groups"
                          :allowFiltering='true' :filterSettings='filterOptions' :allowSorting='true'
                          :showBusyIndicator="true" :dataBound="restoreGroupsSelection"
                          :rowSelected="groupsSelectionChanged" :rowDeselected="groupsSelectionChanged">
                    <e-columns>
                        <e-column type='checkbox' width='50' textAlign='Left'></e-column>
                        <e-column field='name' :headerText="$t('Name').toUpperCase()" textAlign='Left'></e-column>
                    </e-columns>
                </ejs-grid>
            </div>
        </div>
    </div>
</template>

<script>
import { Sort, Page, Filter, Toolbar, Search } from '@syncfusion/ej2-vue-grids';
import {
    getAllUsers,
    getSystemsWithCountries,
    getExistingGroups,
    addScheduledReport,
    editScheduledReport,
    previewScheduledReport,
    sendScheduledReportNow
} from '@/api/api';
import Button from '../Button.vue';

// Musi odpowiadac ReportFrequencyEnum na backendzie.
const FREQUENCY_DAILY = 0;
const FREQUENCY_WEEKLY = 1;
const FREQUENCY_MONTHLY = 2;

// Backend ogranicza dzien miesiaca do 28, zeby raport wyszedl takze w lutym.
const MAX_DAY_OF_MONTH = 28;

export default {
    components: { Button },
    props: {
        // Istniejaca konfiguracja do edycji, albo null przy dodawaniu nowej.
        current: {
            type: Object,
            default: null
        }
    },
    provide: {
        grid: [ Sort, Page, Filter, Toolbar, Search ]
    },
    data() {
        return {
            report: {
                id: null,
                name: '',
                isActive: true,
                frequency: FREQUENCY_DAILY,
                sendHour: 5,
                sendMinute: 0,
                dayOfWeek: 1,
                dayOfMonth: 1,
                skipWeekends: false,
                language: 0
            },
            users: [],
            systems: [],
            groups: [],
            selectedUsers: [],
            selectedSystems: [],
            selectedGroups: [],
            // Zaznaczenia do odtworzenia przy edycji. null = juz odtworzone (albo nie ma czego).
            pendingUserIds: null,
            pendingSystemIds: null,
            pendingGroupIds: null,
            externalEmails: [],
            externalEmailInput: '',
            error: '',
            message: '',

            hours: Array.from({ length: 24 }, (item, index) => index),
            minutes: [0, 15, 30, 45],
            monthDays: Array.from({ length: MAX_DAY_OF_MONTH }, (item, index) => index + 1),
            weekDays: [
                { value: 1, label: 'Monday' },
                { value: 2, label: 'Tuesday' },
                { value: 3, label: 'Wednesday' },
                { value: 4, label: 'Thursday' },
                { value: 5, label: 'Friday' },
                { value: 6, label: 'Saturday' },
                { value: 7, label: 'Sunday' }
            ],

            wrapSettings: { wrapMode: 'Content' },
            selectionOptions: { type: 'Multiple', checkboxOnly: true, persistSelection: false },
            filterOptions: { type: 'Menu' },
            filterCheckbox: { type: 'CheckBox' }
        }
    },
    computed: {
        periodHint() {
            if (this.report.frequency === FREQUENCY_WEEKLY) {
                return 'PeriodHintWeekly';
            }

            if (this.report.frequency === FREQUENCY_MONTHLY) {
                return 'PeriodHintMonthly';
            }

            // Przy pomijaniu weekendow raport dzienny obejmuje w poniedzialek takze sobote i niedziele.
            return this.report.skipWeekends ? 'PeriodHintDailySkipWeekends' : 'PeriodHintDaily';
        },
        // Dla cyklu tygodniowego i miesiecznego trzeba osobno powiedziec o przesunieciu terminu -
        // podpowiedz dzienna mowi o tym sama.
        skipWeekendsHint() {
            if (!this.report.skipWeekends || this.report.frequency === FREQUENCY_DAILY) {
                return '';
            }

            return 'PeriodHintSkipWeekends';
        }
    },
    methods: {
        pad(value) {
            return value < 10 ? `0${value}` : `${value}`;
        },
        async getData() {
            this.users = await getAllUsers();
            this.systems = (await getSystemsWithCountries()).sort((a, b) => this.compareNames(a.systemName, b.systemName));
            this.groups = (await getExistingGroups()).sort((a, b) => this.compareNames(a.name, b.name));
        },
        // Sortowanie naturalne jak na liscie systemow (Systems.vue) - "geno 2" przed "geno 11".
        compareNames(a, b) {
            return (a || '').localeCompare(b || '', undefined, { numeric: true, sensitivity: 'base' });
        },
        usersSelectionChanged() {
            this.selectedUsers = this.$refs.users_grid.getSelectedRecords();
        },
        systemsSelectionChanged() {
            this.selectedSystems = this.$refs.systems_grid.getSelectedRecords();
        },
        groupsSelectionChanged() {
            this.selectedGroups = this.$refs.groups_grid.getSelectedRecords();
        },
        addExternalEmail() {
            var email = (this.externalEmailInput || '').trim();

            if (email === '') {
                return;
            }

            if (!this.isEmailValid(email)) {
                this.error = this.$t('InvalidEmail');
                return;
            }

            if (this.externalEmails.some(x => x.toLowerCase() === email.toLowerCase())) {
                this.externalEmailInput = '';
                return;
            }

            this.externalEmails.push(email);
            this.externalEmailInput = '';
            this.error = '';
        },
        removeExternalEmail(email) {
            this.externalEmails = this.externalEmails.filter(x => x !== email);
        },
        isEmailValid(email) {
            var re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
            return re.test(String(email).toLowerCase());
        },
        buildPayload() {
            var recipients = this.selectedUsers.map(x => ({ applicationUserId: x.id }))
                .concat(this.externalEmails.map(email => ({ email: email })));

            var scopes = this.selectedSystems.map(x => ({ systemId: x.id }))
                .concat(this.selectedGroups.map(x => ({ systemGroupId: x.id })));

            return {
                id: this.report.id,
                name: this.report.name,
                isActive: this.report.isActive,
                frequency: this.report.frequency,
                sendHour: this.report.sendHour,
                sendMinute: this.report.sendMinute,
                dayOfWeek: this.report.frequency === FREQUENCY_WEEKLY ? this.report.dayOfWeek : null,
                dayOfMonth: this.report.frequency === FREQUENCY_MONTHLY ? this.report.dayOfMonth : null,
                skipWeekends: this.report.skipWeekends,
                language: this.report.language,
                recipients: recipients,
                scopes: scopes
            };
        },
        checkRequirements() {
            this.error = '';

            if (!this.report.name || this.report.name.trim() === '') {
                this.error = this.$t('ReportNameRequired');
                return;
            }

            if (this.selectedUsers.length === 0 && this.externalEmails.length === 0) {
                this.error = this.$t('SelectAtLeastOneRecipient');
                return;
            }

            if (this.selectedSystems.length === 0 && this.selectedGroups.length === 0) {
                this.error = this.$t('SelectAtLeastOneSystemOrGroup');
            }
        },
        async save() {
            this.checkRequirements();

            if (this.error !== '') {
                return;
            }

            var payload = this.buildPayload();

            try {
                if (payload.id) {
                    await editScheduledReport(payload);
                } else {
                    await addScheduledReport(payload);
                }

                this.$emit('saved');
            } catch (e) {
                this.error = this.readError(e);
            }
        },
        async preview() {
            try {
                var html = await previewScheduledReport(this.report.id);
                var previewWindow = window.open('', '_blank');

                if (previewWindow) {
                    previewWindow.document.write(html);
                    previewWindow.document.close();
                }
            } catch (e) {
                this.error = this.readError(e);
            }
        },
        async sendNow() {
            try {
                var result = await sendScheduledReportNow(this.report.id);
                this.error = '';
                this.message = `${this.$t('SentToRecipients')}: ${result.sent}`;
            } catch (e) {
                this.message = '';
                this.error = this.readError(e);
            }
        },
        readError(e) {
            if (e && e.response && e.response.data && e.response.data.error) {
                return e.response.data.error;
            }

            return `${e}`;
        },
        restoreUsersSelection() {
            this.restoreSelection('users_grid', 'pendingUserIds', 'selectedUsers');
        },
        restoreSystemsSelection() {
            this.restoreSelection('systems_grid', 'pendingSystemIds', 'selectedSystems');
        },
        restoreGroupsSelection() {
            this.restoreSelection('groups_grid', 'pendingGroupIds', 'selectedGroups');
        },
        /**
         * Odtworzenie zaznaczen zapisanego raportu. Wolane z dataBound, a nie zaraz po pobraniu
         * danych - siatka renderuje wiersze asynchronicznie po podmianie dataSource i wczesniej
         * getCurrentViewRecords() zwraca jeszcze pusta liste.
         */
        restoreSelection(gridRef, pendingProp, selectedProp) {
            var ids = this[pendingProp];

            if (ids === null) {
                return;
            }

            if (ids.length === 0) {
                this[pendingProp] = null;
                return;
            }

            var grid = this.$refs[gridRef];
            var records = grid ? (grid.getCurrentViewRecords() || []) : [];

            // Pierwszy dataBound leci jeszcze na pustej siatce - czekamy na ten z danymi.
            if (records.length === 0) {
                return;
            }

            // Po pierwszym odtworzeniu czyscimy liste, zeby filtrowanie czy sortowanie
            // nie przywracalo pozycji odznaczonych przez uzytkownika.
            this[pendingProp] = null;

            // Zaznaczenia odtwarzamy po id, nie po nazwie - nazwy nie sa unikalne.
            var indexes = records
                .map((record, index) => (ids.indexOf(record.id) >= 0 ? index : -1))
                .filter(index => index >= 0);

            if (indexes.length === 0) {
                return;
            }

            grid.selectRows(indexes);

            // Stan czytamy wprost z siatki, zamiast polegac na zdarzeniu rowSelected.
            this[selectedProp] = grid.getSelectedRecords();
        },
        applyCurrent() {
            this.report = {
                id: this.current.id,
                name: this.current.name,
                isActive: this.current.isActive,
                frequency: this.current.frequency,
                sendHour: this.current.sendHour,
                sendMinute: this.current.sendMinute,
                dayOfWeek: this.current.dayOfWeek || 1,
                dayOfMonth: this.current.dayOfMonth || 1,
                skipWeekends: this.current.skipWeekends === true,
                language: this.current.language
            };

            var recipients = this.current.recipients || [];
            var scopes = this.current.scopes || [];

            this.externalEmails = recipients.filter(x => !x.applicationUserId && x.email).map(x => x.email);

            this.pendingUserIds = recipients.filter(x => x.applicationUserId).map(x => x.applicationUserId);
            this.pendingSystemIds = scopes.filter(x => x.systemId).map(x => x.systemId);
            this.pendingGroupIds = scopes.filter(x => x.systemGroupId).map(x => x.systemGroupId);
        }
    },
    // Synchronicznie, przed pobraniem danych - inaczej dataBound siatki moglby wyprzedzic
    // rozpakowanie edytowanego raportu i zaznaczenia nie zostalyby odtworzone.
    created() {
        if (this.current) {
            this.applyCurrent();
        }
    },
    async mounted() {
        await this.getData();
    }
}
</script>

<style scoped>
h4 {
    text-align: center;
    font-size: 16px;
    padding-top: 10px;
}

.hint {
    font-size: 11px;
    color: #6b6b6b;
    margin-top: 10px;
}

.error {
    text-align: left !important;
    color: #FD8282;
    font-size: 11px;
    font-weight: bold;
    margin-top: 8px;
}

.success {
    text-align: left !important;
    color: #2e7d32;
    font-size: 11px;
    font-weight: bold;
    margin-top: 8px;
}

#info-container {
    width: 34%;
    padding: 20px;

    background: #FFFFFF;
    border: 1px solid #EFEFEF;
    box-sizing: border-box;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
    border-radius: 5px 0px 0px 5px;

    justify-content: space-between;
    display: flex;
    flex-direction: column;
}

#recipients-container,
#systems-container {
    width: 33%;
    align-items: center;
    justify-content: center;
}

.grid-box {
    height: 240px;
    width: 92%;
    margin: auto;
    padding: 0 0 10px 0;
}

.gallery-container {
    background: #F9F9F9;
    box-shadow: 0px 0px 40px rgba(53, 110, 138, 0.25);
    border-radius: 5px;
}

.info-field {
    border-bottom: 1px solid #E9E9E9;
    margin-bottom: 12px;
    margin-top: 12px;
}

.info-field-title {
    font-style: normal;
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;

    color: #272727;
    margin: 0;
}

.info-field-row {
    width: 100%;
    margin: 0;
    padding-bottom: 5px;
}

.info-field-value {
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 16px;

    color: #272727;
}

.chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    min-height: 24px;
}

.chip {
    background: #E6EEF2;
    color: #34758E;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 11px;
    font-weight: 600;
}

.chip-remove {
    cursor: pointer;
    margin-left: 6px;
    font-weight: 700;
}

select {
    border: none;
    width: 100%;
}

select:focus {
    outline: none;
    border-bottom: 1px solid #0095b2;
}

option {
    padding: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/ScheduledReports/ScheduledReportModal.vue